'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { QaStatus, QrReviewBatch } from '../types';

interface QrReviewCardProps {
  batch: QrReviewBatch;
}

const STATUS_BADGE_CLASSES: Record<QaStatus, string> = {
  in_qa: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending_qa:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  qa_passed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  qa_failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
};

const STATUS_LABEL_KEYS: Record<QaStatus, string> = {
  in_qa: 'statusInQa',
  pending_qa: 'statusPendingQa',
  qa_passed: 'statusQaPassed',
  qa_failed: 'statusQaFailed'
};

export function QrReviewCard({ batch }: QrReviewCardProps) {
  const t = useTranslations('qrReview');
  const router = useRouter();

  const progressPct =
    batch.total_qa_pages > 0
      ? Math.round((batch.reviewed_pages / batch.total_qa_pages) * 100)
      : 0;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: batch_number + qa_status badge */}
        <div className='mb-4 flex items-start justify-between'>
          <span className='truncate font-mono text-sm font-bold'>
            {batch.batch_number}
          </span>
          <Badge
            variant='outline'
            className={cn(
              'shrink-0 border-0 text-xs font-medium',
              STATUS_BADGE_CLASSES[batch.qa_status] ??
                'bg-muted text-muted-foreground'
            )}
          >
            {t(STATUS_LABEL_KEYS[batch.qa_status] ?? batch.qa_status)}
          </Badge>
        </div>

        {/* Middle: details */}
        <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
          <p>
            <span className='font-medium'>{t('columnContainerId')}:</span>{' '}
            {batch.container_id || '—'}
          </p>
          <p>
            <span className='font-medium'>{t('columnClientName')}:</span>{' '}
            {batch.client_name || '—'}
          </p>
          <p>
            <span className='font-medium'>{t('columnScannerId')}:</span>{' '}
            <span className='font-mono'>{batch.scanner_id || '—'}</span>
          </p>
        </div>

        {/* Progress bar */}
        <div className='mb-4'>
          <div className='mb-1 flex items-center justify-between text-xs'>
            <span className='text-muted-foreground'>
              {t('reviewedProgress', {
                reviewed: batch.reviewed_pages,
                total: batch.total_qa_pages
              })}
            </span>
            <span className='text-muted-foreground tabular-nums'>
              {progressPct}%
            </span>
          </div>
          <div className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
            <div
              className='bg-primary h-full rounded-full transition-all'
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Bottom: action */}
        <div className='flex items-center justify-end border-t pt-3'>
          <Button
            size='sm'
            variant='outline'
            onClick={() => router.push(`/dashboard/qr-review/${batch.id}`)}
          >
            {t('openForReview')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
